import React, { useState } from 'react';
import { Trash2, Edit2, Check, Pencil } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ModelPreviewModal from '@/components/ModelPreviewModal';
import {
  IToolData,
  IPostAttachment,
  IAttachmentCategory,
  ILaserOrEndmill,
  IBitUsedUnit,
} from '@/redux/post/post.d';
import {
  AttachmentParentType,
  AttachmentType,
} from '@/redux/attachment/attachment.d';
import { useGetActiveBitsQuery } from '@/redux/bit/bit.api.slice';
import { resolveToolSpecs } from '@/redux/post/post.utils';
import { BitThumbnail } from '@/components/BitDetailDialog';
import { FileNameChip } from './FileNameChip';

type Model = { _id: string; fileName: string; url?: string };

// A tool spec value counts as "present" only when it is a positive, real
// number. Number(undefined|null|''|NaN) is NaN or 0, all of which fail `> 0`,
// so this one comparison covers every absent/invalid case.
const positiveNumber = (value: unknown): number | null => {
  const n = Number(value);
  return n > 0 ? n : null;
};

// Builds the muted subtitle line for a configured tool row, e.g.
// "Laser • 30 W" or "Endmill • 6 mm • 90° • 1.5mmR • 18000 RPM".
// One branch per spec kind — inherently branchy display logic.
// fallow-ignore-next-line complexity
const buildToolSubtitle = (
  specs: ReturnType<typeof resolveToolSpecs>,
  bit?: Parameters<typeof resolveToolSpecs>[1]
): string => {
  const parts: string[] = [specs.laserOrEndmill];

  if (specs.laserOrEndmill === ILaserOrEndmill.LASER) {
    // Laser wattage from the catalog bit (mirrors the editor's laser card).
    const wattage = positiveNumber(bit?.wattage);
    if (wattage !== null) parts.push(`${wattage} W`);
  }
  if (typeof specs.toolUsed !== 'undefined' && specs.toolUnit) {
    parts.push(`${specs.toolUsed} ${specs.toolUnit}`);
  }
  const degree = positiveNumber(specs.vBitDegree);
  if (degree !== null) parts.push(`${degree}°`);

  const tipRadius = positiveNumber(specs.tipRadius);
  if (tipRadius !== null) {
    const unit = specs.toolUnit === IBitUsedUnit.MM ? 'mm' : '"';
    parts.push(`${tipRadius}${unit}R`);
  }
  const rpm = positiveNumber(specs.spindleSpeed);
  if (rpm !== null) parts.push(`${rpm} RPM`);

  return parts.join(' • ');
};

export default function ConfiguredGroupsPanel({
  modelGroups,
  openAccordionItems,
  setOpenAccordionItems,
  handleDeleteModelGroup,
  deleteToolData,
  updateGroupName,
  onEditGroup,
  models,
  getControllerIcon,
  className,
  isWorkflowFinalized,
  showSimplifiedView,
  selectedFileName,
}: {
  modelGroups: {
    _id: string;
    name: string;
    modelIds: string[];
    toolData: IToolData[];
  }[];
  openAccordionItems: string[];
  setOpenAccordionItems: (items: string[]) => void;
  handleDeleteModelGroup: (groupId: string) => void;
  deleteToolData: (groupId: string, toolId: string) => void;
  updateGroupName: (oldGroupName: string, newGroupName: string) => void;
  onEditGroup?: (groupId: string) => void;
  models: Model[];
  getControllerIcon: (
    fileName: string
  ) => React.ComponentType<{ className?: string }> | null;
  className?: string;
  isWorkflowFinalized?: boolean;
  showSimplifiedView?: boolean;
  selectedFileName?: string;
}) {
  const { data: bits = [] } = useGetActiveBitsQuery();

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<IPostAttachment | null>(
    null
  );
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupName, setEditingGroupName] = useState<string>('');

  const canEditGroupName = (group: { _id: string; toolData: IToolData[] }) => {
    // Only allow editing if:
    // 1. The group has tool data (is fully complete)
    // 2. The workflow is finalized (user has completed the process)
    return group.toolData.length > 0 && isWorkflowFinalized;
  };

  const handleStartEditing = (groupId: string, currentName: string) => {
    const group = modelGroups.find((g) => g._id === groupId);
    if (!group || !canEditGroupName(group)) {
      return; // Don't allow editing if conditions aren't met
    }
    setEditingGroupId(groupId);
    setEditingGroupName(currentName);
  };

  const handleSaveEdit = () => {
    if (editingGroupId && editingGroupName.trim()) {
      const group = modelGroups.find((g) => g._id === editingGroupId);
      if (group && group.name !== editingGroupName.trim()) {
        updateGroupName(group.name, editingGroupName.trim());
      }
    }
    setEditingGroupId(null);
    setEditingGroupName('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveEdit();
    }
  };

  const handleGroupAccordionChange = (groupValue: string) => {
    const isCurrentlyOpen = openAccordionItems.includes(groupValue);
    if (isCurrentlyOpen) {
      setOpenAccordionItems(
        openAccordionItems.filter((item) => item !== groupValue)
      );
    } else {
      const group = modelGroups.find((g) => g._id === groupValue);
      const newItems = [groupValue];
      if (group && group.modelIds.length > 0 && !showSimplifiedView) {
        newItems.push(`${groupValue}-files`);
      }
      setOpenAccordionItems(newItems);
    }
  };

  const handleSubAccordionChange = (groupValue: string, subValue: string) => {
    const isCurrentlyOpen = openAccordionItems.includes(subValue);
    if (isCurrentlyOpen) {
      setOpenAccordionItems(
        openAccordionItems.filter((item) => item !== subValue)
      );
    } else {
      const otherSubValues = openAccordionItems.filter(
        (item) =>
          item !== `${groupValue}-toolData` && item !== `${groupValue}-files`
      );
      setOpenAccordionItems([...otherSubValues, subValue]);
    }
  };

  return (
    <Card className={`h-full flex flex-col ${className || ''}`}>
      <CardHeader className="flex-shrink-0 sticky top-0 z-10 bg-card border-b py-2 px-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium">
            {showSimplifiedView ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span>Tool data for</span>
                {selectedFileName && (
                  <FileNameChip
                    fileName={selectedFileName}
                    showRemove={false}
                    size="xs"
                  />
                )}
                <span>Gcode</span>
              </div>
            ) : (
              'Configured Gcode File Groups'
            )}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="flex-1 p-0">
        <ScrollArea className="h-full px-3 py-2">
          {modelGroups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm font-medium mb-2">
                {showSimplifiedView
                  ? 'No tool data added yet'
                  : 'No configured groups yet'}
              </p>
              <p className="text-xs text-muted-foreground">
                {showSimplifiedView
                  ? 'Add tool data using the editor on the left'
                  : 'Select Gcode files from the left panel and add tool data to create your first group.'}
              </p>
            </div>
          ) : (
            <Accordion
              type="multiple"
              className="w-full space-y-2"
              value={openAccordionItems}
              onValueChange={(values) => {
                const groupValues = (values as string[]).filter(
                  (v) => !v.includes('-')
                );
                if (groupValues.length > 0) {
                  handleGroupAccordionChange(groupValues[0]);
                }
              }}
            >
              {modelGroups.map((group) => (
                <AccordionItem
                  key={group._id}
                  value={group._id}
                  className="border rounded-lg"
                >
                  <AccordionTrigger className="px-3 py-2 hover:no-underline group">
                    <div className="flex items-center justify-between w-full pr-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-md bg-green-600 flex items-center justify-center flex-shrink-0">
                          <svg
                            className="w-4 h-4 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                          </svg>
                        </div>
                        <div className="min-w-0 flex-1">
                          {!showSimplifiedView && (
                            <div className="font-medium text-left text-sm leading-snug">
                              {editingGroupId === group._id ? (
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={editingGroupName}
                                    onChange={(e) =>
                                      setEditingGroupName(e.target.value)
                                    }
                                    onKeyDown={handleKeyDown}
                                    onBlur={handleSaveEdit}
                                    className="h-6 text-sm font-medium"
                                    autoFocus
                                  />
                                  <Button
                                    size="sm"
                                    onClick={handleSaveEdit}
                                    className="h-6 w-6 rounded-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center p-0 min-w-0"
                                  >
                                    <Check className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div
                                  className={`flex items-center gap-2 rounded px-1 py-0.5 -mx-1 -my-0.5 ${
                                    canEditGroupName(group)
                                      ? 'cursor-pointer hover:bg-muted/50'
                                      : 'cursor-default'
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartEditing(group._id, group.name);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleStartEditing(group._id, group.name);
                                    }
                                  }}
                                  role="button"
                                  tabIndex={canEditGroupName(group) ? 0 : -1}
                                  title={
                                    canEditGroupName(group)
                                      ? 'Click to edit group name'
                                      : 'Complete the workflow to edit group name'
                                  }
                                >
                                  <span>{group.name}</span>
                                  {canEditGroupName(group) && (
                                    <Edit2 className="h-3 w-3 opacity-60 hover:opacity-100 transition-opacity" />
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground text-left mt-0.5">
                            {showSimplifiedView ? (
                              <>
                                {group.toolData.length} tool
                                {group.toolData.length !== 1 ? 's' : ''}{' '}
                                configured
                              </>
                            ) : (
                              <>
                                {group.modelIds.length} Gcode file
                                {group.modelIds.length !== 1 ? 's' : ''} •{' '}
                                {group.toolData.length} tool
                                {group.toolData.length !== 1 ? 's' : ''}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      {!showSimplifiedView && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {isWorkflowFinalized && onEditGroup && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditGroup(group._id);
                              }}
                              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                              title="Edit group tools"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteModelGroup(group._id);
                            }}
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3">
                    <div className="space-y-3 ml-2">
                      {group.toolData.length > 0 && (
                        <Accordion
                          type="single"
                          collapsible
                          className="w-full"
                          value={
                            openAccordionItems.includes(`${group._id}-toolData`)
                              ? `${group._id}-toolData`
                              : ''
                          }
                          onValueChange={() =>
                            handleSubAccordionChange(
                              group._id,
                              `${group._id}-toolData`
                            )
                          }
                        >
                          <AccordionItem
                            value={`${group._id}-toolData`}
                            className="border rounded-lg"
                          >
                            <AccordionTrigger className="px-3 py-2 hover:no-underline">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded bg-blue-100 flex items-center justify-center">
                                  <svg
                                    className="w-4 h-4 text-blue-600"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                                <span className="font-medium text-sm">
                                  Tool Data ({group.toolData.length})
                                </span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-3 pb-2">
                              <div className="space-y-2 ml-2">
                                {group.toolData.map((tool, index) => {
                                  const bit =
                                    tool.bit ??
                                    bits.find((b) => b._id === tool.bitId);
                                  const specs = resolveToolSpecs(tool, bit);
                                  const brand = bit?.brands.find(
                                    (b) => b._id === tool.brandId
                                  );
                                  return (
                                    <div
                                      key={tool._id}
                                      className="flex items-center justify-between p-3 bg-muted rounded-lg"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-6 h-6 rounded bg-primary/10 flex items-center justify-center text-xs font-medium">
                                          {index + 1}
                                        </div>
                                        <BitThumbnail bit={bit} />
                                        <div>
                                          <div className="font-medium text-sm">
                                            {specs.toolType ||
                                              (specs.laserOrEndmill ===
                                              ILaserOrEndmill.LASER
                                                ? 'Laser'
                                                : 'Endmill')}
                                          </div>
                                          <div className="text-xs text-muted-foreground">
                                            {buildToolSubtitle(specs, bit)}
                                          </div>
                                          {brand && (
                                            <div className="text-xs text-muted-foreground">
                                              {brand.name}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() =>
                                          deleteToolData(
                                            group._id,
                                            String(index)
                                          )
                                        }
                                        className="text-destructive hover:text-destructive/90"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  );
                                })}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      )}

                      {!showSimplifiedView && (
                        <Accordion
                          type="single"
                          collapsible
                          className="w-full"
                          value={
                            openAccordionItems.includes(`${group._id}-files`)
                              ? `${group._id}-files`
                              : ''
                          }
                          onValueChange={() =>
                            handleSubAccordionChange(
                              group._id,
                              `${group._id}-files`
                            )
                          }
                        >
                          <AccordionItem
                            value={`${group._id}-files`}
                            className="border rounded-lg"
                          >
                            <AccordionTrigger className="px-3 py-2 hover:no-underline">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded bg-green-100 flex items-center justify-center">
                                  <svg
                                    className="w-4 h-4 text-green-600"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                                <span className="font-medium text-sm">
                                  Files ({group.modelIds.length})
                                </span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-3 pb-2">
                              <div className="space-y-2 ml-2">
                                {group.modelIds.map((modelId) => {
                                  const model = models.find(
                                    (m) => m._id === modelId
                                  );
                                  if (!model) return null;
                                  return (
                                    <div
                                      key={modelId}
                                      className="flex items-center gap-3 p-2 rounded-md bg-muted/50 hover:bg-muted/70 cursor-pointer transition-colors"
                                      role="button"
                                      tabIndex={0}
                                      onClick={() => {
                                        setSelectedModel({
                                          _id: model._id,
                                          parentId: '',
                                          parentType: AttachmentParentType.POST,
                                          fileName: model.fileName,
                                          user: '',
                                          resourcePath: model.url || '',
                                          url: model.url || '',
                                          fileSize: 0,
                                          fileHash: '',
                                          resourceType: AttachmentType.model,
                                          category:
                                            IAttachmentCategory.postModel,
                                          downloadCount: 0,
                                          createdAt: '',
                                          updatedAt: '',
                                        });
                                        setIsPreviewModalOpen(true);
                                      }}
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === 'Enter' ||
                                          e.key === ' '
                                        ) {
                                          e.preventDefault();
                                          setSelectedModel({
                                            _id: model._id,
                                            parentId: '',
                                            parentType:
                                              AttachmentParentType.POST,
                                            fileName: model.fileName,
                                            user: '',
                                            resourcePath: model.url || '',
                                            url: model.url || '',
                                            fileSize: 0,
                                            fileHash: '',
                                            resourceType: AttachmentType.model,
                                            category:
                                              IAttachmentCategory.postModel,
                                            downloadCount: 0,
                                            createdAt: '',
                                            updatedAt: '',
                                          });
                                          setIsPreviewModalOpen(true);
                                        }
                                      }}
                                      title="Click to preview model"
                                    >
                                      <div className="w-4 h-4 rounded bg-white border border-gray-300 flex items-center justify-center">
                                        {(() => {
                                          const ControllerIcon =
                                            getControllerIcon(model.fileName);
                                          return ControllerIcon ? (
                                            <ControllerIcon className="w-2.5 h-2.5 text-gray-700" />
                                          ) : (
                                            <svg
                                              className="w-2.5 h-2.5 text-gray-700"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                                            </svg>
                                          );
                                        })()}
                                      </div>
                                      <span className="text-sm font-medium truncate min-w-0 flex-1">
                                        {model.fileName}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </ScrollArea>
      </CardContent>

      <ModelPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => {
          setIsPreviewModalOpen(false);
          setSelectedModel(null);
        }}
        model={selectedModel}
      />
    </Card>
  );
}
